import { Component, ViewEncapsulation, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UserSandbox } from '../../user.sandbox';
import { TimelapsePollingService } from '../../../api/services/timelapse.service';

export interface TimelapseDialogData {
  projectDates?: string[];
  cameras?: any[];
}

@Component({
  selector: 'app-timelapse-dialog',
  templateUrl: './timelapse-dialog.component.html',
  styleUrls: ['./timelapse-dialog.component.css', '../../containers/timelapse-page/timelapse-page.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class TimelapseDialogComponent implements OnDestroy {
  videoUploaded: boolean = false; // toggles form vs progress view
  isProcessing: boolean = false;
  isCheckingFeasability: boolean = false;
  progressStatus: string = '';
  status: string = 'INIT'; // status of the video generation process
  progressValue: number = 0; // 0 to 100 based on steps
  showProgress: boolean = true; // toggles visibility of progress view
  videoStartTime: number = 6;
  videoEndTime: number = 18;
  videoFolder: string = "Cam 1";
  videoName: string;
  model1: NgbDateStruct; // Start date
  model2: NgbDateStruct; // End date
  selectedStartDate: string;
  selectedEndDate: string;
  selectedDays: number[] = [1, 2, 3, 4, 5];
  projectDates: string[] = this.data.projectDates ? this.data.projectDates : [];
  cameras: any[] = this.data.cameras ? this.data.cameras : [];
  pollingActive: boolean = true; // add this flag
  private pollTimeoutId: any;

  totalImages: number;
  maxImages: number;
  proposedMaxDate: string;
  acceleratorOptions: number[] = [2, 4, 8, 10];
  chosenAccelerator: number = 0;

  // total steps: Fetching, Downloading, Creating, Uploading, Done
  progressSteps: { [key: string]: number } = {
    "Fetching images...": 10,
    "Downloading images...": 30,
    "Creating timelapse...": 60,
    "Uploading timelapse...": 75,
    "Process complete!": 100,
    "Error during timelapse generation": 100
  };

  constructor(
    public dialogRef: MatDialogRef<TimelapseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TimelapseDialogData,
    private sandbox: UserSandbox,
    private timelapsePollingService: TimelapsePollingService // Inject the polling service
  ) {
    // initialize videoName using defaults
    this.videoName = "Timelapse_" + this.videoFolder + "_" +
      this.videoStartTime + "H:" + this.videoEndTime + "H_" + new Date().getTime();
   }

  onClose(): void {
    this.stopPolling();
    this.dialogRef.close();
  }

  onVideoNameChange(): void {
    if (this.model1 && this.model2) {
      this.selectedStartDate = this.model1.year + '-' + String(this.model1.month).padStart(2, '0') + '-' + String(this.model1.day).padStart(2, '0');
      this.selectedEndDate = this.model2.year + '-' + String(this.model2.month).padStart(2, '0') + '-' + String(this.model2.day).padStart(2, '0');
    } 
    // else {
    //   this.selectedStartDate = '';
    //   this.selectedEndDate = '';
    // }

    this.videoName = "Timelapse_" + this.videoFolder + "_" + this.selectedStartDate + "To" + this.selectedEndDate + "_" +
      this.videoStartTime + "H:" + this.videoEndTime + "H_" + new Date().getTime();
  }

  endTimeOptionArray() {
    return [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24].filter(a => a > this.videoStartTime);
  }

  isDateSelectable(date: NgbDateStruct): boolean {
    if (!this.projectDates || this.projectDates.length === 0) {
      return true;
    }
    const dateStr = date.year + '-' +
      String(date.month).padStart(2, '0') + '-' +
      String(date.day).padStart(2, '0');
    return this.projectDates.includes(dateStr);
  }

  isDisabled = (date: NgbDateStruct, current: { month: number, year: number }): boolean => {
    return !this.isDateSelectable(date);
  }

  toggleWeekDay(day: number): void {
    const index = this.selectedDays.indexOf(day);
    if (index > -1) {
      this.selectedDays.splice(index, 1);
    } else {
      this.selectedDays.push(day);
    }
  }

  generateVideo() {
    this.sandbox.getSelectedProject().subscribe(project => {
      const params = {
        projectName: project.folder,
        cam: this.videoFolder.toLowerCase().replace(/\s/g, ""),
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate,
        startHour: +this.videoStartTime,
        endHour: +this.videoEndTime,
        days: this.selectedDays,
        videoName: this.videoName,
        operation: "generate",
        accelerator: this.chosenAccelerator || 1
      }
      // Switch view: hide form, show progress view and enable polling
      this.status = "GENERATING";
      this.videoUploaded = true;
      this.isProcessing = true;
      this.showProgress = true;
      this.pollingActive = true; // start polling
      this.progressStatus = "Fetching images...";
      this.progressValue = this.progressSteps["Fetching images..."];
      this.pollTimeoutId = setTimeout(() => this.pollProgress(project.folder), 3000);
      this.timelapsePollingService.generateVideoWithNotification(params)
        .subscribe(response => {
          console.log("Video generation response:", response);
          // Optionally handle any response here
        });
    });
  }

  acceptNewDateRange() {
    this.selectedStartDate = this.proposedMaxDate;
    this.chosenAccelerator = 1;
    this.checkFeasability();
  }

  acceptAccelerator() {
    this.checkFeasability();
  }

  isAcceleratorDisabled(accelerator: number): boolean {
    return accelerator < this.totalImages / this.maxImages;
  }

  checkFeasability() {
    this.sandbox.getSelectedProject().subscribe(project => {
      const params = {
        projectName: project.folder,
        cam: this.videoFolder.toLowerCase().replace(/\s/g, ""),
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate,
        startHour: +this.videoStartTime,
        endHour: +this.videoEndTime,
        days: this.selectedDays,
        videoName: this.videoName,
        operation: "count",
        accelerator: this.chosenAccelerator || 1
      }
      // Switch view: hide form, show progress view and enable polling
      this.status = "CHECKING";
      this.isCheckingFeasability = true;
      this.timelapsePollingService.generateVideoWithNotification(params)
        .subscribe(response => {
          if (response.imageCount > response.maxImageCount) {
            this.isCheckingFeasability = false;
            // Parse the date string, subtract one day, and format it back
            const maxDate = new Date(response.maxDate3);
            this.totalImages = response.imageCount;
            this.maxImages = response.maxImageCount;
            maxDate.setDate(maxDate.getDate() + 1);
            this.proposedMaxDate = maxDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          } else {
            this.generateVideo();
          }
      });
    });
  }

  async pollProgress(projectName: string) {
    try {
      const response = await fetch("https://4f3l2zzgjwtwdbxiowvjulvymm0owoax.lambda-url.eu-central-1.on.aws/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectName }),
      });
      const data = await response.json();
      this.progressStatus = data.status;
      if (this.progressSteps[data.status] !== undefined) {
        this.progressValue = this.progressSteps[data.status];
      }
      // Only continue polling if pollingActive flag is true
      if (this.pollingActive && (data.status !== "Process complete!" && data.status !== "Error during timelapse generation")) {
        this.pollTimeoutId = setTimeout(() => this.pollProgress(projectName), 3000); // poll every 2 seconds
      } else {
        this.isProcessing = false;
        if (data.status === "Process complete!") {
          this.progressStatus = `Timelapse generated! You can find ${this.videoName} in your MyFiles section.`;
        }
      }
    } catch (error) {
      console.error("Error during polling:", error);
      this.progressStatus = "Error retrieving progress";
      this.isProcessing = false;
    }
  }

  hideProgress(): void {
    this.stopPolling();
    this.showProgress = false;
  }

  stopPolling(): void {
    this.pollingActive = false;
    if (this.pollTimeoutId) {
      clearTimeout(this.pollTimeoutId);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}